import React, { useState } from 'react';
import FoilTable from '../components/FoilTable';
import { M3CSRow, M3CS_COLUMNS, M3CS_FILTERS, M3CS_SEARCH, M3CS_VALIDATE, M3CS_PREPARE_SEARCH, M3CSSearchContext } from '../configs/m3cs.config';
import { resolveFoilAvailability, resolveRowDetails, indexRows, filterSelectedRows } from '../util/rows';
import Filter from '../components/Filter';
import styles from './Modal.module.css';
import headerStyles from '../components/Header.module.css';
import { useProductModel } from '../util/useProductModel';
import { filterRows } from '../util/filter';
import Modal from './Modal';
import { fetchBatches, excludeCurrentBatch } from '../util/batch';
import Footer from '../components/Footer';
import HeaderButtons from '../components/HeaderButtons';
import HeaderField from '../components/HeaderField';
import { useReloadable } from '../util/useReloadable';
import ErrorMessage from '../components/ErrorMessage';
import { resolvePastedFoilNumbers } from '../util/clipboard';


const M3CS = ({ createMode }: { createMode: boolean }) => {
    const [product, updateProduct, productRef, initialProductRef] = useProductModel();
    const [rows, setRows] = useState<M3CSRow[]>([]);
    const [indexedRows, setIndexedRows] = useState<{ [foilNumber: string]: M3CSRow }>();
    const [query, setQuery] = useState<string>('');
    const [activeFilters, setActiveFilters] = useState<number[]>([]);

    const { loading, reload, error } = useReloadable(async () => {
        const [inputBatches, existingCBatches, existingLBatches] = await fetchBatches(['^S-', '^C-', '^L-']);
        const existingOutputBatches = existingCBatches.concat(existingLBatches);
        const outputBatches = excludeCurrentBatch(existingOutputBatches, productRef.current?.identifier);

        const rows: M3CSRow[] = resolveFoilAvailability<M3CSRow>(inputBatches, outputBatches, 'cs_foils');
        const availableRows = rows.filter((row) => row.available);
        const detailedRows: M3CSRow[] = await resolveRowDetails(availableRows, M3CS_COLUMNS, inputBatches);

        setRows(detailedRows);
        setIndexedRows(indexRows(detailedRows));
    }, []);

    const batchNumber = product?.identifier || '';
    const visibleRows = filterRows<M3CSRow, M3CSSearchContext>(rows, M3CS_FILTERS, M3CS_SEARCH, activeFilters, M3CS_PREPARE_SEARCH(query));
    const selectedRowKeys = product?.parameters?.cs_foils || [];
    const selectedRows = filterSelectedRows(selectedRowKeys, indexedRows);
    const hasSelectedRows = selectedRows.length > 0;

    const updateSelectedRowKeys = (selectedRowKeys: string[]) => {
        updateProduct({
            parameters: {
                cs_foils: selectedRowKeys,
            }
        });
    }

    const updateBatchNumber = (newBatchNumber: string) => {
        updateProduct({
            identifier: newBatchNumber
        });
    }

    const handlePasteFoils = async (foilNumbers: string[]) => {
        const newSelectedRowKeys = await resolvePastedFoilNumbers(foilNumbers, selectedRowKeys, [], indexedRows);

        if (!newSelectedRowKeys) {
            return;
        }

        updateSelectedRowKeys(newSelectedRowKeys);
    }

    if (error) {
        return (
            <ErrorMessage error={error} />
        )
    }

    return (
        <Modal
            loading={loading}
            onBeforeSubmit={() => M3CS_VALIDATE(product, createMode, initialProductRef.current)}
        >
            <header className={headerStyles.topHeader}>
                <HeaderField
                    id={'batchNumber'}
                    label={'Batch number'}
                    value={batchNumber}
                    onChange={updateBatchNumber}
                />
            </header>
            <header className={headerStyles.bottomHeader}>
                <Filter
                    query={query}
                    setQuery={setQuery}
                    filters={M3CS_FILTERS}
                    activeFilters={activeFilters}
                    setActiveFilters={setActiveFilters}
                />
                <HeaderButtons<M3CSRow>
                    columns={M3CS_COLUMNS}
                    rows={rows}
                    exportFilename={'Azumuta_export_M3CS'}
                    onPasteFoils={handlePasteFoils}
                    onReload={reload}
                />
            </header>
            <section className={styles.inputTableWrapper}>
                <FoilTable<M3CSRow>
                    columns={M3CS_COLUMNS}
                    rows={visibleRows}
                    selectedRowKeys={selectedRowKeys}
                    sortable={true}
                    onRowToggle={updateSelectedRowKeys}
                />
            </section>
            {hasSelectedRows &&
                <section className={styles.outputTableWrapper}>
                    <FoilTable<M3CSRow>
                        columns={M3CS_COLUMNS}
                        rows={selectedRows}
                        selectedRowKeys={selectedRowKeys}
                        onRowToggle={updateSelectedRowKeys}
                    />
                </section>
            }
            {hasSelectedRows &&
                <Footer
                    selectedRowCount={selectedRowKeys.length}
                    onClear={() => updateSelectedRowKeys([])}
                />
            }
        </Modal>
    );
}

export default M3CS;

